import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

interface MetricOutputVersionStatus {
  ranVersion: number
  currentVersion: number
  isStale: boolean
}

export function getMetricOutputVersionStatus(
  metricOutput: { metric_version_number?: number | null },
  currentVersionNumber?: number | null,
): MetricOutputVersionStatus {
  const ranVersion = metricOutput.metric_version_number ?? 1
  const currentVersion = currentVersionNumber ?? 1
  return {
    ranVersion,
    currentVersion,
    isStale: ranVersion !== currentVersion,
  }
}

/**
 * Every message a metric output points at. The count is not capped: a metric
 * that cites many messages cites them, and dropping the whole set past some
 * count leaves the surfaces that jump to one of them with nothing to mark.
 */
export function getHighlightedMessageIndexes(
  metricOutput: RunMetricOutput,
): number[] {
  if (metricOutput.subvalues_by_timestamp?.length) {
    const indexes = [
      ...new Set(
        metricOutput.subvalues_by_timestamp
          .filter((subvalue) => subvalue.message_index != null)
          .map((subvalue) => subvalue.message_index as number),
      ),
    ]
    if (indexes.length > 0) {
      return indexes
    }
  }

  const legacyIndexes = metricOutput.result_json?.matching_message_indices
  if (Array.isArray(legacyIndexes)) {
    return legacyIndexes.map(Number)
  }

  return []
}

export function getCompletedMetricOutputs(
  runRows: RunRowLike[],
  metricId: string,
): RunMetricOutput[] {
  return runRows
    .map((row) => row.metric_outputs?.[metricId])
    .filter(
      (output): output is RunMetricOutput =>
        output !== undefined &&
        (output.error_status === MetricErrorStatus.COMPLETED ||
          !output.error_status),
    )
}
